import { useState } from 'react';
import styles from './StyleGuide.module.css';
import { LANGUAGES } from '../config.js';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ChoiceCard,
  ChoiceGrid,
  ChoiceList,
  ChoiceRow,
  Field,
  LiveBadge,
  Logo,
  MatchRow,
  Metric,
  Modal,
  ModalRow,
  OtpInput,
  Segmented,
  Sparkline,
  SplitBar,
  StatTile,
  Stepper,
  TextField,
  TierCard,
  Toggle,
} from '../components/ui';

/*
 * Living catalogue of the shared component kit.
 *
 * Everything on this page is rendered from the real components, so if a
 * component changes, this page changes with it. It is a development reference —
 * it is not part of the marketing site or the app, and it uses placeholder
 * content on purpose (no backend calls happen here).
 */

const SPORTS = [
  { emoji: '⚽', label: 'Soccer' },
  { emoji: '🏀', label: 'Basketball' },
  { emoji: '🏈', label: 'Am. Football' },
  { emoji: '🤾', label: 'Handball' },
  { emoji: '🏒', label: 'Hockey' },
  { emoji: '🥊', label: 'MMA' },
  { emoji: '🏉', label: 'Rugby' },
  { emoji: '🏐', label: 'Volleyball' },
  { emoji: '🏏', label: 'Cricket' },
];

const ACCURACY_TREND = [61, 63, 62, 66, 65, 69, 68, 71, 72];

function Section({ number, title, description, children }) {
  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <span className="eyebrow">{number}</span>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {children}
    </section>
  );
}

function GroupLabel({ children }) {
  return <div className={styles.groupLabel}>{children}</div>;
}

export function StyleGuide() {
  const [otp, setOtp] = useState('');
  const [lang, setLang] = useState('en');
  const [range, setRange] = useState('30d');
  const [notify, setNotify] = useState(true);
  const [promos, setPromos] = useState(false);
  const [sport, setSport] = useState('Soccer');
  const [league, setLeague] = useState('Premier League');
  const [step, setStep] = useState(3);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className={styles.page}>
      <header className={styles.top}>
        <div className={styles.wrap}>
          <Logo size={30} />
          <span className={styles.tag}>Style guide</span>
        </div>
      </header>

      <div className={styles.wrap}>
        <div className={styles.hero}>
          <span className="eyebrow">Stage 1 · Foundation</span>
          <h1>
            The shared <span className="grad-text">component kit</span>.
          </h1>
          <p>
            Every screen in the marketing site and the web app is built from these pieces, so the
            product always looks like one product. Tokens live in{' '}
            <code>src/styles/tokens.css</code>; components in <code>src/components/ui/</code>.
          </p>
        </div>

        {/* ---------------- COLOUR ---------------- */}
        <Section
          number="01 · Foundations"
          title="Colour"
          description="Navy-leaning blacks keep surfaces premium on a data-dense screen. The orange→gold accent is energy — CTAs, the predicted winner, key stats, nowhere else."
        >
          <GroupLabel>Surfaces</GroupLabel>
          <div className={styles.swatches}>
            <Swatch color="#0A0E18" name="Ink" note="base" />
            <Swatch color="#141A28" name="Slate" note="card" />
            <Swatch color="#1E2638" name="Steel" note="elevated" />
            <Swatch color="#2A3447" name="Hairline" note="border" />
          </div>

          <GroupLabel>Accent</GroupLabel>
          <div className={styles.swatches}>
            <Swatch color="#FF8A14" name="Quack Orange" />
            <Swatch color="#FFC531" name="Quack Gold" />
            <Swatch
              color="linear-gradient(135deg,#FF8A14,#FFC531)"
              name="Accent gradient"
              note="135°"
            />
          </div>

          <GroupLabel>Text</GroupLabel>
          <div className={styles.swatches}>
            <Swatch color="#EDF1F7" name="Mist" note="primary" />
            <Swatch color="#9AA6BD" name="Fog" note="secondary" />
            <Swatch color="#5E6B83" name="Haze" note="muted" />
          </div>

          <GroupLabel>Semantic</GroupLabel>
          <div className={styles.swatches}>
            <Swatch color="#3DDC97" name="Win green" />
            <Swatch color="#FB5E72" name="Loss red" />
            <Swatch color="#5B9DFF" name="Info blue" />
          </div>
        </Section>

        {/* ---------------- TYPE ---------------- */}
        <Section
          number="02 · Foundations"
          title="Typography"
          description="Space Grotesk for headings and big numbers; Inter for body and UI, with full Cyrillic coverage. Numbers use tabular figures so percentages never jitter."
        >
          <TypeRow tag="Display" spec="Space Grotesk 700 · 44">
            <span className={styles.tDisplay}>Top Picks, 72% accuracy</span>
          </TypeRow>
          <TypeRow tag="H1" spec="Space Grotesk 600 · 34">
            <span className={styles.tH1}>Generate a forecast</span>
          </TypeRow>
          <TypeRow tag="H2" spec="Space Grotesk 600 · 26">
            <span className={styles.tH2}>Today&rsquo;s high-confidence picks</span>
          </TypeRow>
          <TypeRow tag="H3" spec="Space Grotesk 500 · 20">
            <span className={styles.tH3}>Manchester City vs Arsenal</span>
          </TypeRow>
          <TypeRow tag="Body" spec="Inter 400 · 16">
            <span className={styles.tBody}>
              Probability-based predictions across nine sports, generated daily from live bookmaker
              odds.
            </span>
          </TypeRow>
          <TypeRow tag="Small · Cyrillic" spec="Inter 400 · 13">
            <span className={styles.tSmall}>Прогнозы на спорт, которым можно доверять.</span>
          </TypeRow>
          <TypeRow tag="Label" spec="Inter 600 · 12 · caps">
            <span className="eyebrow">Win probability</span>
          </TypeRow>
        </Section>

        {/* ---------------- BUTTONS ---------------- */}
        <Section
          number="03 · Library"
          title="Buttons"
          description="One component, five variants, three sizes. Primary is the accent gradient and is used sparingly — one per view."
        >
          <div className={styles.row}>
            <Button>Generate forecast 🦆</Button>
            <Button variant="secondary">View Top Picks</Button>
            <Button variant="ghost">Maybe later</Button>
            <Button variant="pro">Upgrade to VIP</Button>
            <Button variant="danger">Delete account</Button>
          </div>

          <GroupLabel>Sizes</GroupLabel>
          <div className={styles.row}>
            <Button size="sm">Small</Button>
            <Button>Default</Button>
            <Button size="lg">Large</Button>
          </div>

          <GroupLabel>States</GroupLabel>
          <div className={styles.row}>
            <Button loading>Sending code</Button>
            <Button disabled>Disabled</Button>
            <Button variant="secondary" disabled>
              Current plan
            </Button>
          </div>
        </Section>

        {/* ---------------- INPUTS ---------------- */}
        <Section
          number="04 · Library"
          title="Inputs"
          description="The login flow lives here: an email field, then the six-digit code. The code boxes auto-advance, step back on backspace, and accept a pasted code."
        >
          <div className={styles.two}>
            <div>
              <TextField
                label="Email address"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
              />
              <TextField
                label="Email address (error state)"
                type="email"
                defaultValue="not-an-email"
                error="Enter a valid email address."
              />
              <Field label="6-digit code — try typing or pasting" hint={`Value: "${otp}"`}>
                <OtpInput value={otp} onChange={setOtp} />
              </Field>
            </div>

            <div>
              <Field label="Language">
                <Segmented
                  options={LANGUAGES.map((l) => ({ value: l.code, label: l.short }))}
                  value={lang}
                  onChange={setLang}
                  ariaLabel="Language"
                />
              </Field>
              <Field label="Range">
                <Segmented
                  options={[
                    { value: '7d', label: '7d' },
                    { value: '30d', label: '30d' },
                    { value: 'all', label: 'All-time' },
                  ]}
                  value={range}
                  onChange={setRange}
                  ariaLabel="Date range"
                />
              </Field>
              <Field label="Notifications">
                <div className={styles.toggleRow}>
                  <span className="muted">Daily Top Picks</span>
                  <Toggle checked={notify} onChange={setNotify} label="Daily Top Picks" />
                </div>
                <div className={styles.toggleRow}>
                  <span className="muted">Promotions</span>
                  <Toggle checked={promos} onChange={setPromos} label="Promotions" />
                </div>
              </Field>
            </div>
          </div>
        </Section>

        {/* ---------------- BADGES ---------------- */}
        <Section
          number="05 · Library"
          title="Badges & pills"
          description="Confidence ratings, sport tags, graded outcomes, and the live accuracy pill."
        >
          <div className={styles.row}>
            <Badge tone="high" dot>
              High confidence
            </Badge>
            <Badge tone="med" dot>
              Medium
            </Badge>
            <Badge tone="low" dot>
              Low
            </Badge>
            <Badge tone="sport">⚽ Soccer</Badge>
            <Badge tone="sport">🏀 Basketball</Badge>
            <Badge tone="pro">Premium</Badge>
            <Badge tone="win" dot>
              Correct
            </Badge>
            <Badge tone="loss" dot>
              Missed
            </Badge>
            <Badge tone="info">Web account</Badge>
            <LiveBadge>Live accuracy 72%</LiveBadge>
          </div>
        </Section>

        {/* ---------------- SIGNATURE ---------------- */}
        <Section
          number="06 · Signature"
          title="The win-probability split bar"
          description="The one device people will remember. Every forecast resolves to this: two sides, the split, and the predicted winner glowing in the accent gradient."
        >
          <div className={styles.two}>
            <Card padding="lg" radius="xl" gradient>
              <div className={styles.league}>Premier League · Today 20:00</div>
              <div className={styles.teams}>
                <span className={styles.team}>
                  <span className={styles.crest}>🔵</span> Man City
                </span>
                <span className={styles.vs}>VS</span>
                <span className={styles.team}>
                  Arsenal <span className={styles.crest}>🔴</span>
                </span>
              </div>
              <SplitBar
                homeProbability={64}
                winner="home"
                size="lg"
                homeLabel="Man City"
                awayLabel="Arsenal"
              />
              <div className={styles.verdict}>
                <span className="muted">
                  Predicted winner · <b>Man City</b>
                </span>
                <Badge tone="high" dot>
                  High confidence
                </Badge>
              </div>
            </Card>

            <Metric
              label="Top Picks accuracy · last 30 days"
              value="72%"
              trend="▲ 4 pts vs previous 30 days · 148 picks graded"
              sparkline={ACCURACY_TREND}
              live
            />
          </div>

          <GroupLabel>Sizes &amp; the away-side winner</GroupLabel>
          <div className={styles.stack}>
            <SplitBar homeProbability={58} winner="home" size="sm" />
            <SplitBar homeProbability={41} winner="away" size="md" />
            <SplitBar homeProbability={92} winner="home" size="lg" />
          </div>

          <GroupLabel>Compact rows — free and subscription-gated</GroupLabel>
          <div className={styles.two}>
            <MatchRow
              league="Premier League"
              confidence="high"
              confidenceLabel="High"
              homeTeam="Man City"
              awayTeam="Arsenal"
              homeProbability={64}
              winner="home"
              predictedWinner="Man City"
              kickoff="Today 20:00"
            />
            <MatchRow
              league="NBA"
              confidence="high"
              confidenceLabel="High"
              homeTeam="Lakers"
              awayTeam="Celtics"
              homeProbability={58}
              winner="home"
              predictedWinner="Lakers"
              kickoff="Today 02:30"
              locked
              lockMessage="Unlock all of today's Top Picks"
            />
          </div>

          <GroupLabel>Stat tiles &amp; the full trend chart</GroupLabel>
          <div className={styles.three}>
            <StatTile value="72%" label="Top Picks accuracy · 30d" gradient />
            <StatTile value="148" label="Picks graded" />
            <StatTile value="▲4" label="Points vs prior 30d" />
          </div>
          <Card className={styles.chartCard}>
            <div className={styles.chartLabel}>Accuracy over time</div>
            <Sparkline points={ACCURACY_TREND} height={160} gridLines />
          </Card>
        </Section>

        {/* ---------------- WIZARD ---------------- */}
        <Section
          number="07 · Library"
          title="Forecast wizard pieces"
          description="Sport → date → league → match → forecast. The stepper tracks progress; completed steps are clickable to go back."
        >
          <Stepper
            steps={['Sport', 'Date', 'League', 'Match', 'Forecast']}
            current={step}
            onStepClick={setStep}
          />
          <div className={styles.row} style={{ margin: '18px 0 24px' }}>
            <Button size="sm" variant="secondary" onClick={() => setStep((s) => Math.max(1, s - 1))}>
              Back
            </Button>
            <Button size="sm" onClick={() => setStep((s) => Math.min(5, s + 1))}>
              Continue
            </Button>
          </div>

          <GroupLabel>Step 1 — tile grid</GroupLabel>
          <ChoiceGrid>
            {SPORTS.map((s) => (
              <ChoiceCard
                key={s.label}
                emoji={s.emoji}
                label={s.label}
                selected={sport === s.label}
                onClick={() => setSport(s.label)}
              />
            ))}
          </ChoiceGrid>

          <GroupLabel>Steps 2–4 — row list</GroupLabel>
          <ChoiceList>
            {[
              { label: '🏴 Premier League', detail: '5 matches' },
              { label: '🇪🇸 La Liga', detail: '4 matches' },
              { label: '🇮🇹 Serie A', detail: '3 matches' },
            ].map((l) => (
              <ChoiceRow
                key={l.label}
                label={l.label}
                detail={l.detail}
                selected={league === l.label.slice(3)}
                onClick={() => setLeague(l.label.slice(3))}
              />
            ))}
          </ChoiceList>
        </Section>

        {/* ---------------- TIERS + MODAL ---------------- */}
        <Section
          number="08 · Library"
          title="Subscription tiers & dialogs"
          description="Prices shown here are placeholders for layout only. In the real screens every amount comes from GET /api/web/pricing — the client never decides what anything costs."
        >
          <div className={styles.three}>
            <TierCard
              name="Free"
              price="$0"
              period=""
              features={['One daily curated pick', 'Live accuracy widget', '3 forecasts / day']}
              current
              ctaLabel="Current plan"
            />
            <TierCard
              name="Basic"
              price="$9.99"
              features={['Unlimited match forecasts', 'All supported sports and leagues']}
              ctaLabel="Upgrade to Basic"
              onCta={() => setModalOpen(true)}
            />
            <TierCard
              name="Premium"
              price="$24.99"
              featured
              features={['Everything in Basic', 'Daily Top Picks digest', 'On-demand Top Picks']}
              ctaLabel="Upgrade to Premium"
              onCta={() => setModalOpen(true)}
            />
          </div>

          <div className={styles.row} style={{ marginTop: 20 }}>
            <Button variant="secondary" onClick={() => setModalOpen(true)}>
              Open dialog
            </Button>
          </div>

          <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Upgrade to Premium">
            <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              Secure crypto checkout via NOWPayments.
            </p>
            <ModalRow label="Plan" value="Premium · 30 days" />
            <ModalRow label="Total" value="$24.99" />
            <p className="muted" style={{ fontSize: 13, margin: '18px 0' }}>
              You&rsquo;ll pick your coin on the NOWPayments checkout page.
            </p>
            <Button block onClick={() => setModalOpen(false)}>
              Continue to payment
            </Button>
          </Modal>
        </Section>

        {/* ---------------- NAV ---------------- */}
        <Section
          number="09 · Library"
          title="Navigation & brand"
          description="Sidebar rows, the sticky top bar, and the mark in its three finishes. The app shell that assembles these arrives in Stage 3."
        >
          <div className={styles.two}>
            <Card>
              <GroupLabel>Sidebar rows</GroupLabel>
              <div className={styles.navDemo}>
                <span className={`${styles.fakeNav} ${styles.fakeNavActive}`}>
                  <span className={styles.fakeIc}>🏠</span> Home
                </span>
                <span className={styles.fakeNav}>
                  <span className={styles.fakeIc}>🎯</span> New forecast
                </span>
                <span className={styles.fakeNav}>
                  <span className={styles.fakeIc}>⭐</span> Top Picks
                </span>
                <span className={styles.fakeNav}>
                  <span className={styles.fakeIc}>🕑</span> History
                </span>
                <span className={styles.fakeNav}>
                  <span className={styles.fakeIc}>📈</span> Track record
                </span>
              </div>
            </Card>

            <Card>
              <GroupLabel>Top bar contents</GroupLabel>
              <div className={styles.topbarDemo}>
                <h3 style={{ fontSize: 19 }}>Home</h3>
                <div className={styles.topbarRight}>
                  <Segmented
                    size="sm"
                    options={LANGUAGES.map((l) => ({ value: l.code, label: l.short }))}
                    value={lang}
                    onChange={setLang}
                    ariaLabel="Language"
                  />
                  <Avatar />
                </div>
              </div>

              <GroupLabel>The mark</GroupLabel>
              <div className={styles.marks}>
                <div className={styles.markBox}>
                  <Logo size={48} withText={false} />
                  <span>Gradient</span>
                </div>
                <div className={styles.markBox}>
                  <Logo size={48} variant="mono" withText={false} />
                  <span>Mono</span>
                </div>
                <div className={`${styles.markBox} ${styles.markLight}`}>
                  <Logo size={48} variant="dark" withText={false} />
                  <span>On light</span>
                </div>
              </div>
            </Card>
          </div>
        </Section>

        <footer className={styles.footer}>
          Don Quack · Stage 1 foundation — tokens and shared component library.
          <br />
          Next: Stage 2, the marketing site.
        </footer>
      </div>
    </div>
  );
}

function Swatch({ color, name, note }) {
  return (
    <div className={styles.sw}>
      <div className={styles.swChip} style={{ background: color }} />
      <div className={styles.swInfo}>
        <div className={styles.swName}>{name}</div>
        <div className={styles.swHex}>
          {color.startsWith('#') ? color : '—'}
          {note ? ` · ${note}` : ''}
        </div>
      </div>
    </div>
  );
}

function TypeRow({ tag, spec, children }) {
  return (
    <div className={styles.typeRow}>
      <span className={styles.typeTag}>{tag}</span>
      {children}
      <span className={styles.typeSpec}>{spec}</span>
    </div>
  );
}
